import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem, removeStorageItem } from '../utils/storageUtils';

const useResizableLayout = ({
  defaultPanelPosition = 'right',
  defaultPanelWidth = 300,
  defaultPanelHeight = 215,
  defaultIsCollapsed = false,
  minPanelWidth = 250,
  maxPanelWidth = 600,
  minPanelHeight = 150,
  maxPanelHeight = 400,
  storageKey = 'resizableLayoutState'
} = {}) => {
  const [panelPosition, setPanelPosition] = useState(defaultPanelPosition);
  const [panelWidth, setPanelWidth] = useState(defaultPanelWidth);
  const [panelHeight, setPanelHeight] = useState(defaultPanelHeight);
  const [isCollapsed, setIsCollapsed] = useState(defaultIsCollapsed);

  // Load saved state from storage on mount
  useEffect(() => {
    const savedState = getStorageItem(storageKey);
    if (savedState) {
      try {
        // Validate and apply saved state
        if (savedState.panelPosition && ['right', 'bottom'].includes(savedState.panelPosition)) {
          setPanelPosition(savedState.panelPosition);
        }
        
        if (savedState.panelWidth && savedState.panelWidth >= minPanelWidth && savedState.panelWidth <= maxPanelWidth) {
          setPanelWidth(savedState.panelWidth);
        }
        
        if (savedState.panelHeight && savedState.panelHeight >= minPanelHeight && savedState.panelHeight <= maxPanelHeight) {
          setPanelHeight(savedState.panelHeight);
        }
        
        if (typeof savedState.isCollapsed === 'boolean') {
          setIsCollapsed(savedState.isCollapsed);
        }
      } catch (error) {
        console.warn('Failed to load resizable layout state:', error);
      }
    }
  }, [storageKey, minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight]);

  // Save state to storage whenever it changes
  const saveState = useCallback(() => {
    const state = {
      panelPosition,
      panelWidth,
      panelHeight,
      isCollapsed
    };
    
    setStorageItem(storageKey, state, {
      priority: 'normal',
      maxAge: 30 * 24 * 60 * 60 * 1000 // 30 days
    });
  }, [panelPosition, panelWidth, panelHeight, isCollapsed, storageKey]);

  // Save state whenever it changes
  useEffect(() => {
    saveState();
  }, [saveState]);

  // Handle panel resize
  const handleResize = useCallback(({ width, height, position }) => {
    if (position === 'right' && width !== undefined) {
      const constrainedWidth = Math.max(minPanelWidth, Math.min(maxPanelWidth, width));
      setPanelWidth(constrainedWidth);
    } else if (position === 'bottom' && height !== undefined) {
      const constrainedHeight = Math.max(minPanelHeight, Math.min(maxPanelHeight, height));
      setPanelHeight(constrainedHeight);
    }
  }, [minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight]);

  // Handle panel position change
  const handlePositionChange = useCallback((newPosition) => {
    if (['right', 'bottom'].includes(newPosition)) {
      setPanelPosition(newPosition);
    }
  }, []);

  // Handle panel collapse/expand
  const handleCollapseToggle = useCallback((collapsed) => {
    setIsCollapsed(collapsed);
  }, []);

  // Reset to defaults
  const resetLayout = useCallback(() => {
    setPanelPosition(defaultPanelPosition);
    setPanelWidth(defaultPanelWidth);
    setPanelHeight(defaultPanelHeight);
    setIsCollapsed(defaultIsCollapsed);
    
    // Clear saved state
    removeStorageItem(storageKey);
  }, [defaultPanelPosition, defaultPanelWidth, defaultPanelHeight, defaultIsCollapsed, storageKey]);

  // Get current layout info
  const getLayoutInfo = useCallback(() => {
    return {
      panelPosition,
      panelWidth,
      panelHeight,
      isCollapsed,
      effectiveWidth: isCollapsed ? 0 : panelWidth,
      effectiveHeight: isCollapsed ? 0 : panelHeight,
      canResize: !isCollapsed,
      constraints: {
        minPanelWidth,
        maxPanelWidth,
        minPanelHeight,
        maxPanelHeight
      }
    };
  }, [panelPosition, panelWidth, panelHeight, isCollapsed, minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight]);

  // Check if layout is at default state
  const isDefaultLayout = useCallback(() => {
    return (
      panelPosition === defaultPanelPosition &&
      panelWidth === defaultPanelWidth &&
      panelHeight === defaultPanelHeight &&
      isCollapsed === defaultIsCollapsed
    );
  }, [panelPosition, panelWidth, panelHeight, isCollapsed, defaultPanelPosition, defaultPanelWidth, defaultPanelHeight, defaultIsCollapsed]);

  // Responsive layout adjustments
  const handleResponsiveLayout = useCallback((screenWidth, screenHeight) => {
    // Don't auto-collapse on mobile - let user control panel state
    // Removed auto-collapse logic to fix mobile panel stability
    
    // Switch to bottom panel on mobile screens but keep panel open
    if (screenWidth < 768 && panelPosition === 'right') {
      setPanelPosition('bottom');
    }
    
    // Adjust panel dimensions for screen size
    if (panelPosition === 'right') {
      const maxAllowedWidth = Math.floor(screenWidth * 0.4); // Max 40% of screen width
      if (panelWidth > maxAllowedWidth) {
        setPanelWidth(Math.max(minPanelWidth, maxAllowedWidth));
      }
    } else {
      const maxAllowedHeight = Math.floor(screenHeight * 0.4); // Max 40% of screen height
      if (panelHeight > maxAllowedHeight) {
        setPanelHeight(Math.max(minPanelHeight, maxAllowedHeight));
      }
    }
  }, [panelPosition, panelWidth, panelHeight, minPanelWidth, minPanelHeight]);

  // Listen for window resize events
  useEffect(() => {
    const handleWindowResize = () => {
      handleResponsiveLayout(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleWindowResize);
    
    // Initial check
    handleResponsiveLayout(window.innerWidth, window.innerHeight);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleResponsiveLayout]);

  return {
    // State
    panelPosition,
    panelWidth,
    panelHeight,
    isCollapsed,
    
    // Actions
    handleResize,
    handlePositionChange,
    handleCollapseToggle,
    resetLayout,
    
    // Utilities
    getLayoutInfo,
    isDefaultLayout,
    
    // Direct setters (for advanced use cases)
    setPanelPosition,
    setPanelWidth,
    setPanelHeight,
    setIsCollapsed
  };
};

export default useResizableLayout;